import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ROUTINE_EMAIL_TEMPLATE_KEYS,
  ROUTINE_EMAIL_TEMPLATE_DEFAULTS,
} from '../../components/settings/RoutineEmailsSettings';
import {
  format,
  subDays,
  subMonths,
  startOfDay,
  endOfDay,
  isWithinInterval,
  parseISO,
} from 'date-fns';
import {
  ArrowLeft,
  CalendarClock,
  Check,
  CloudDownload,
  Filter,
  HelpCircle,
  Link2,
  Loader2,
  Mail,
  Paperclip,
  Printer,
  RefreshCw,
  Send,
  X,
} from 'lucide-react';
import api, { clientsAPI, settingsAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import { getClientDisplayName } from './clientsForms';

const STATEMENT_PREF_KEY = ROUTINE_EMAIL_TEMPLATE_KEYS.statement;

function mergeStatementEmailTemplate(savedTemplates) {
  const d = ROUTINE_EMAIL_TEMPLATE_DEFAULTS[STATEMENT_PREF_KEY];
  const t = savedTemplates?.[STATEMENT_PREF_KEY];
  return {
    subject: t?.subject != null ? String(t.subject) : d.subject,
    body: t?.body != null ? String(t.body) : d.body,
  };
}

const DEFAULT_STATEMENT_EMAIL = mergeStatementEmailTemplate(undefined);

function buildContactList(client, apiContacts) {
  const list = [];
  if (!client?.id) return list;

  if (client.email) {
    const clientType = client.clientType || client.client_type;
    const name =
      clientType === 'company'
        ? client.companyName || client.company_name || 'Primary contact'
        : `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() ||
          'Primary contact';
    list.push({
      id: 'primary',
      name,
      email: client.email,
      isPrimary: true,
    });
  }

  (apiContacts || []).forEach((contact) => {
    if (contact.email) {
      list.push({
        id: contact.id,
        name:
          `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email,
        email: contact.email,
        position: contact.position,
        isPrimary: false,
      });
    }
  });

  return list;
}

const STATEMENT_DEFAULTS_KEY = 'sparxmate_client_statement_defaults';

const stripHtml = (s) => {
  if (!s || typeof s !== 'string') return '';
  return s.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const readDefaults = () => {
  try {
    const raw = localStorage.getItem(STATEMENT_DEFAULTS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const fmtNum = (n) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(typeof n === 'number' ? n : parseFloat(n || 0));

const MoneyCell = ({ amount, className = '' }) => (
  <span className={`tabular-nums ${className}`}>
    {fmtNum(amount)}
    <span className="text-[10px] font-normal text-gray-400 dark:text-gray-500 ml-1">GBP</span>
  </span>
);

function invoiceSortDate(inv) {
  const raw = inv.dateIssued || inv.dateCreated || inv.createdAt;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function getPeriodRange(periodKey, customFrom, customTo) {
  const now = new Date();
  if (periodKey === 'all') return null;
  if (periodKey === 'custom') {
    if (!customFrom || !customTo) return null;
    try {
      const start = startOfDay(parseISO(customFrom));
      const end = endOfDay(parseISO(customTo));
      return start.getTime() <= end.getTime() ? { start, end } : null;
    } catch {
      return null;
    }
  }
  if (periodKey === '7d') {
    return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
  }
  if (periodKey === '30d') {
    return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
  }
  if (periodKey === '1m') {
    return { start: startOfDay(subMonths(now, 1)), end: endOfDay(now) };
  }
  const months =
    periodKey === '3m' ? 3 : periodKey === '6m' ? 6 : periodKey === '12m' ? 12 : 12;
  return { start: startOfDay(subMonths(now, months)), end: endOfDay(now) };
}

export default function ClientStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();

  const [availableContacts, setAvailableContacts] = useState([]);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [sendEmailAddresses, setSendEmailAddresses] = useState([]);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [sendEmailSuccess, setSendEmailSuccess] = useState('');
  const [sendModalError, setSendModalError] = useState('');
  const [stmtTemplate, setStmtTemplate] = useState(DEFAULT_STATEMENT_EMAIL);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_STATEMENT_EMAIL.subject);
  const [emailBody, setEmailBody] = useState(DEFAULT_STATEMENT_EMAIL.body);
  const [ccSelf, setCcSelf] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const [copyLinkState, setCopyLinkState] = useState('idle');

  const defaultsRef = useRef(readDefaults());
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [logoObjectUrl, setLogoObjectUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const d0 = defaultsRef.current || {};
  const [period, setPeriod] = useState(d0.period || '12m');
  const [showCustomRange, setShowCustomRange] = useState(d0.period === 'custom');
  const [customFrom, setCustomFrom] = useState(d0.customFrom || '');
  const [customTo, setCustomTo] = useState(d0.customTo || '');
  const [hidePaidInvoices, setHidePaidInvoices] = useState(d0.hidePaidInvoices === true);
  const [hidePaymentsColumn, setHidePaymentsColumn] = useState(d0.hidePaymentsColumn !== false);

  /** Saved `clients.preferences.statementSchedule` (monthly automation) */
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDay, setScheduleDay] = useState(1);
  const [schedulePeriod, setSchedulePeriod] = useState('12m');
  const [scheduleCustomFrom, setScheduleCustomFrom] = useState('');
  const [scheduleCustomTo, setScheduleCustomTo] = useState('');
  const [scheduleHidePaid, setScheduleHidePaid] = useState(false);
  /** Same as statement view: when true, the Paid column is hidden. */
  const [scheduleHidePaymentsColumn, setScheduleHidePaymentsColumn] = useState(true);
  const [scheduleExpiresDays, setScheduleExpiresDays] = useState(30);
  const [scheduleAttachPdf, setScheduleAttachPdf] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [showScheduleCustomRange, setShowScheduleCustomRange] = useState(false);

  const logoBlobRef = useRef(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const [clientRes, activeRes, archivedRes, settingsRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get('/invoices', { params: { client_id: id, limit: 500, view: 'active' } }),
        api.get('/invoices', { params: { client_id: id, limit: 500, view: 'archived' } }),
        settingsAPI.get().catch(() => ({ settings: null })),
      ]);
      setClient(clientRes.data);
      setTenantSettings(settingsRes.settings || null);

      const map = new Map();
      for (const inv of activeRes.data.invoices || []) map.set(inv.id, inv);
      for (const inv of archivedRes.data.invoices || []) map.set(inv.id, inv);
      const merged = [...map.values()].sort((a, b) => invoiceSortDate(a) - invoiceSortDate(b));
      setInvoices(merged);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statement');
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!client?.id) return;
    const ss = client.preferences?.statementSchedule;
    setScheduleEnabled(ss?.enabled === true);
    setScheduleDay(
      typeof ss?.dayOfMonth === 'number' && ss.dayOfMonth >= 1 && ss.dayOfMonth <= 28
        ? ss.dayOfMonth
        : 1
    );
    const p = ss?.period && typeof ss.period === 'string' ? ss.period : '12m';
    setSchedulePeriod(p);
    setShowScheduleCustomRange(p === 'custom');
    setScheduleCustomFrom(typeof ss?.customFrom === 'string' ? ss.customFrom : '');
    setScheduleCustomTo(typeof ss?.customTo === 'string' ? ss.customTo : '');
    setScheduleHidePaid(ss?.hidePaidInvoices === true);
    setScheduleHidePaymentsColumn(ss?.hidePaymentsColumn !== false);
    setScheduleExpiresDays(
      typeof ss?.expiresInDays === 'number' && ss.expiresInDays >= 1 && ss.expiresInDays <= 90
        ? ss.expiresInDays
        : 30
    );
    setScheduleAttachPdf(ss?.attachPdf === true);
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await settingsAPI.get();
        const saved = data?.settings?.preferences?.routineEmails?.templates;
        if (!cancelled) setStmtTemplate(mergeStatementEmailTemplate(saved));
      } catch (e) {
        console.error('Failed to load statement email template', e);
        if (!cancelled) setStmtTemplate(mergeStatementEmailTemplate(undefined));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setEmailSubject(stmtTemplate.subject);
    setEmailBody(stmtTemplate.body);
  }, [stmtTemplate]);

  const refreshContacts = useCallback(async (c) => {
    if (!c?.id) {
      setAvailableContacts([]);
      return;
    }
    try {
      const response = await api.get(`/clients/${c.id}/contacts`);
      const contacts = response.data.contacts || [];
      setAvailableContacts(buildContactList(c, contacts));
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setAvailableContacts(buildContactList(c, []));
    }
  }, []);

  useEffect(() => {
    if (client?.id) {
      refreshContacts(client);
    } else {
      setAvailableContacts([]);
    }
  }, [client, refreshContacts]);

  useEffect(() => {
    let cancelled = false;
    if (logoBlobRef.current) {
      URL.revokeObjectURL(logoBlobRef.current);
      logoBlobRef.current = null;
    }
    setLogoObjectUrl(null);

    const logoUrl = tenantSettings?.logo_url;
    if (!logoUrl || !token || logoUrl !== '/api/settings/logo') {
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const res = await fetch('/api/settings/logo', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled || !res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        logoBlobRef.current = url;
        setLogoObjectUrl(url);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      if (logoBlobRef.current) {
        URL.revokeObjectURL(logoBlobRef.current);
        logoBlobRef.current = null;
      }
    };
  }, [tenantSettings?.logo_url, token]);

  const periodRange = useMemo(
    () => getPeriodRange(period, customFrom, customTo),
    [period, customFrom, customTo]
  );

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (hidePaidInvoices && inv.status === 'paid-in-full') return false;
      if (!periodRange) return true;
      const t = invoiceSortDate(inv);
      if (!t) return true;
      const d = new Date(t);
      return isWithinInterval(d, { start: periodRange.start, end: periodRange.end });
    });
  }, [invoices, hidePaidInvoices, periodRange]);

  const statementDate = format(new Date(), 'dd/MM/yyyy');

  const summary = useMemo(() => {
    let invoiced = 0;
    let payments = 0;
    for (const inv of filteredInvoices) {
      invoiced += parseFloat(inv.total) || 0;
      payments += parseFloat(inv.amountPaid) || 0;
    }
    const outstanding =
      client?.current_balance != null && client.current_balance !== ''
        ? parseFloat(client.current_balance) || 0
        : filteredInvoices.reduce((acc, inv) => acc + (parseFloat(inv.balanceDue) || 0), 0);
    return { invoiced, payments, credits: null, outstanding };
  }, [filteredInvoices, client]);

  const rows = useMemo(() => {
    return filteredInvoices.map((inv) => {
      const total = parseFloat(inv.total) || 0;
      const paid = parseFloat(inv.amountPaid) || 0;
      const rawDue = parseFloat(inv.balanceDue);
      const due = Number.isFinite(rawDue) ? rawDue : Math.max(0, total - paid);
      const rawD = inv.dateIssued || inv.dateCreated || inv.createdAt;
      const notesPlain = stripHtml(inv.notes || '');
      const desc =
        notesPlain ||
        (inv.reference && String(inv.reference).trim()) ||
        `Invoice #${inv.invoiceNum}`;
      return {
        id: inv.id,
        date: rawD ? format(new Date(rawD), 'dd/MM/yyyy') : '—',
        num: inv.invoiceNum,
        reference: inv.reference || '—',
        description: desc,
        hasNotes: !!notesPlain,
        total,
        paid,
        due,
        status: inv.status,
      };
    });
  }, [filteredInvoices]);

  const name = getClientDisplayName(client);

  const companyLines = useMemo(() => {
    const s = tenantSettings || {};
    const lines = [];
    if (s.company_name) lines.push(s.company_name);
    if (s.address_line1) lines.push(s.address_line1);
    if (s.address_line2) lines.push(s.address_line2);
    const cityLine = [s.city, s.county].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    if (s.postcode) lines.push(s.postcode);
    return lines;
  }, [tenantSettings]);

  const clientAddressLines = useMemo(() => {
    if (!client) return [];
    const lines = [];
    lines.push(getClientDisplayName(client));
    if (client.address_line1) lines.push(client.address_line1);
    if (client.address_line2) lines.push(client.address_line2);
    const cityLine = [client.city, client.county].filter(Boolean).join(', ');
    if (cityLine) lines.push(cityLine);
    if (client.postcode) lines.push(client.postcode);
    return lines;
  }, [client]);

  const displayLogoSrc =
    tenantSettings?.logo_url === '/api/settings/logo' ? logoObjectUrl : tenantSettings?.logo_url || null;

  const saveViewDefaults = () => {
    try {
      localStorage.setItem(
        STATEMENT_DEFAULTS_KEY,
        JSON.stringify({
          period,
          customFrom,
          customTo,
          hidePaidInvoices,
          hidePaymentsColumn,
        })
      );
      toastSuccess('Statement view defaults saved on this device.');
    } catch {
      toastError('Could not save defaults.');
    }
  };

  const statementViewPayload = () => ({
    period,
    customFrom,
    customTo,
    hidePaidInvoices,
    hidePaymentsColumn,
  });

  const copyCurrentFiltersIntoSchedule = () => {
    setSchedulePeriod(period);
    setShowScheduleCustomRange(period === 'custom');
    setScheduleCustomFrom(customFrom);
    setScheduleCustomTo(customTo);
    setScheduleHidePaid(hidePaidInvoices);
    setScheduleHidePaymentsColumn(hidePaymentsColumn);
    toastSuccess('Automation content updated from the current statement view.');
  };

  const saveStatementSchedule = async () => {
    if (!id) return;
    if (schedulePeriod === 'custom' && (!scheduleCustomFrom?.trim() || !scheduleCustomTo?.trim())) {
      toastError('Choose both dates for a custom period, or pick a preset.');
      return;
    }
    const expires = Math.min(90, Math.max(1, parseInt(String(scheduleExpiresDays), 10) || 30));
    try {
      setScheduleSaving(true);
      const statementSchedule = {
        enabled: scheduleEnabled,
        dayOfMonth: scheduleDay,
        period: schedulePeriod,
        hidePaidInvoices: scheduleHidePaid,
        hidePaymentsColumn: scheduleHidePaymentsColumn,
        expiresInDays: expires,
        attachPdf: scheduleAttachPdf,
      };
      if (schedulePeriod === 'custom') {
        statementSchedule.customFrom = scheduleCustomFrom.trim();
        statementSchedule.customTo = scheduleCustomTo.trim();
      } else {
        statementSchedule.customFrom = '';
        statementSchedule.customTo = '';
      }
      const res = await clientsAPI.update(id, { preferences: { statementSchedule } });
      if (res?.client) {
        setClient((c) =>
          c ? { ...c, ...res.client, preferences: res.client.preferences } : c
        );
      }
      toastSuccess('Statement automation saved.');
    } catch (e) {
      toastError(e.response?.data?.error || e.message || 'Could not save automation.');
    } finally {
      setScheduleSaving(false);
    }
  };

  const toggleRecipientEmail = (email) => {
    setSendEmailAddresses((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const openSendStatementModal = () => {
    setSendEmailSuccess('');
    setSendModalError('');
    setCcSelf(false);
    setAttachPdf(false);
    setCopyLinkState('idle');
    setEmailSubject(stmtTemplate.subject);
    setEmailBody(stmtTemplate.body);
    const primary = availableContacts.find((c) => c.isPrimary);
    if (primary) {
      setSendEmailAddresses([primary.email]);
    } else if (availableContacts[0]?.email) {
      setSendEmailAddresses([availableContacts[0].email]);
    } else {
      setSendEmailAddresses([]);
    }
    setSendEmailModalOpen(true);
  };

  const handleCopyStatementLink = async () => {
    if (!id) return;
    try {
      setSendModalError('');
      const res = await clientsAPI.generateStatementLink(id, {
        expiresInDays: 30,
        ...statementViewPayload(),
      });
      await navigator.clipboard.writeText(res.publicUrl);
      setCopyLinkState('copied');
      setTimeout(() => setCopyLinkState('idle'), 2500);
    } catch (e) {
      setSendModalError(e.message || 'Could not create link');
    }
  };

  const handleSendStatementEmail = async () => {
    if (!id || sendEmailAddresses.length === 0) {
      setSendModalError('Please select at least one recipient');
      return;
    }
    if (!emailSubject.trim()) {
      setSendModalError('Please enter a subject');
      return;
    }
    if (!emailBody.trim()) {
      setSendModalError('Please enter a message');
      return;
    }
    const base = {
      expiresInDays: 30,
      subject: emailSubject.trim(),
      messageText: emailBody,
      attachPdf,
      ...statementViewPayload(),
    };
    try {
      setSendEmailLoading(true);
      setSendModalError('');
      setSendEmailSuccess('');
      for (let i = 0; i < sendEmailAddresses.length; i++) {
        const em = sendEmailAddresses[i];
        const contact = availableContacts.find((c) => c.email === em);
        const recipientFirstName = contact?.name?.trim().split(/\s+/)[0] || undefined;
        await clientsAPI.sendStatementEmail(id, {
          ...base,
          email: em.trim(),
          ccSelf: ccSelf && i === 0,
          recipientFirstName,
        });
      }
      setSendEmailSuccess(`Statement link sent to ${sendEmailAddresses.length} recipient(s).`);
      setSendEmailAddresses([]);
      setTimeout(() => {
        setSendEmailModalOpen(false);
        setSendEmailSuccess('');
      }, 2000);
    } catch (e) {
      setSendModalError(e.message || 'Failed to send statement email');
    } finally {
      setSendEmailLoading(false);
    }
  };

  const exportCsv = () => {
    const headers = ['Date', 'Reference', 'Description', 'Amount', hidePaymentsColumn ? '' : 'Paid'].filter(
      Boolean
    );
    const lines = [headers.join(',')];
    for (const r of rows) {
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const base = [r.date, `#${r.num}`, r.description, fmtNum(r.total)];
      if (!hidePaymentsColumn) base.push(fmtNum(r.paid));
      lines.push(base.map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement-${(name || 'client').replace(/[^a-z0-9]+/gi, '-')}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !client) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-16 text-center text-xs text-[var(--ds-text-muted)]">
          Loading statement…
        </div>
      </Layout>
    );
  }

  if (error && !client) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <p className="text-xs text-[var(--ds-status-danger)] mb-4">{error}</p>
          <Button variant="secondary" size="sm" onClick={() => navigate('/tenant/clients')}>
            ← Clients
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        <ToastContainer />
        <div className="border-b border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-[var(--ds-shadow-card)] print:hidden">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Back to client"
                onClick={() => navigate(`/tenant/clients/${id}`)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openSendStatementModal}
                  disabled={availableContacts.length === 0}
                  title={
                    availableContacts.length === 0
                      ? 'Add an email on the client or a contact with an email'
                      : undefined
                  }
                  className="gap-1 text-xs h-8"
                >
                  <Send className="h-3.5 w-3.5" />
                  Send by email
                </Button>
                <Button variant="secondary" size="sm" onClick={exportCsv} className="gap-1 text-xs h-8">
                  <CloudDownload className="h-3.5 w-3.5" />
                  Export data
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.print()}
                  className="gap-1 text-xs h-8"
                >
                  <Printer className="h-3.5 w-3.5" />
                  Print
                </Button>
                <Button variant="secondary" size="sm" onClick={saveViewDefaults} className="gap-1 text-xs h-8">
                  <Filter className="h-3.5 w-3.5" />
                  Set defaults
                </Button>
              </div>
            </div>
            <h1 className="mt-3 text-lg sm:text-xl font-bold text-[var(--ds-text-primary)]">
              <span className="font-bold">{name}</span>
              <span className="font-normal text-[var(--ds-text-secondary)]"> — Statement</span>
            </h1>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-6 space-y-4 print:py-4 print:px-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between print:hidden">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center text-xs text-[var(--ds-text-primary)]">
              <span className="font-medium text-[var(--ds-text-secondary)] shrink-0">Hide items</span>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePaidInvoices}
                  onChange={(e) => setHidePaidInvoices(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                />
                <span>Paid invoices</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hidePaymentsColumn}
                  onChange={(e) => setHidePaymentsColumn(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                />
                <span>Payments</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer opacity-50 pointer-events-none">
                <input type="checkbox" disabled className="rounded border-gray-300 dark:border-gray-600" />
                <span>Credit notes</span>
              </label>
              <Tooltip
                content="Paid invoices removes fully paid lines from the list. Hiding Payments hides the Paid column. Credit notes are not stored separately yet; this filter is reserved for a future release."
              >
                <button
                  type="button"
                  className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] p-0.5 rounded"
                  aria-label="Help: hide items"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-[var(--ds-text-secondary)] shrink-0">Period</span>
                <Select
                  value={period}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPeriod(v);
                    setShowCustomRange(v === 'custom');
                  }}
                  className="h-8 w-[11rem] text-xs"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="1m">1 month</option>
                  <option value="3m">Last 3 months</option>
                  <option value="6m">Last 6 months</option>
                  <option value="12m">Last 12 months</option>
                  <option value="all">All time</option>
                  <option value="custom">Custom range</option>
                </Select>
                <button
                  type="button"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={() => {
                    setPeriod('custom');
                    setShowCustomRange(true);
                  }}
                >
                  Enter custom date range
                </button>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={load} aria-label="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {showCustomRange && (
            <div className="flex flex-wrap items-end gap-3 print:hidden">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                />
              </div>
            </div>
          )}

          <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-white dark:bg-gray-800 shadow-[var(--ds-shadow-card)] p-4 sm:p-5 print:hidden space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-5 w-5 shrink-0 mt-0.5 text-[var(--ds-text-muted)]" aria-hidden />
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Automatic monthly statement
                  </h2>
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 leading-relaxed max-w-prose">
                    When enabled, we queue a statement email on your chosen day each month (tenant time zone).
                    Sends use the bulk email queue and may arrive within your tenant&apos;s configured send window.
                    Short months use the last valid day (e.g. day 30 → 28 Feb).
                  </p>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={scheduleEnabled}
                  onChange={(e) => setScheduleEnabled(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Enabled</span>
              </label>
            </div>

            {!client?.email?.trim() && (
              <p className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/50 rounded-md px-3 py-2">
                Add a primary email on the client record so automated statements can be sent.
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="stmt-sched-day"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Day of month
                </label>
                <Select
                  id="stmt-sched-day"
                  value={String(scheduleDay)}
                  onChange={(e) => setScheduleDay(parseInt(e.target.value, 10) || 1)}
                  disabled={!scheduleEnabled}
                  className="h-9 w-full text-xs border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={String(d)}>
                      {d}
                    </option>
                  ))}
                </Select>
                <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">1–28 to avoid month-end ambiguity.</p>
              </div>
              <div>
                <label
                  htmlFor="stmt-sched-expires"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Link expires (days)
                </label>
                <input
                  id="stmt-sched-expires"
                  type="number"
                  min={1}
                  max={90}
                  value={scheduleExpiresDays}
                  onChange={(e) => setScheduleExpiresDays(parseInt(e.target.value, 10) || 30)}
                  disabled={!scheduleEnabled}
                  className="w-full h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-1 flex flex-col justify-end">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleAttachPdf}
                    onChange={(e) => setScheduleAttachPdf(e.target.checked)}
                    disabled={!scheduleEnabled}
                    className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Attach PDF (otherwise link-only)</span>
                </label>
              </div>
            </div>

            <div className="border-t border-[var(--ds-border-subtle)] pt-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Content for automated sends
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 text-xs shrink-0"
                  disabled={!scheduleEnabled}
                  onClick={copyCurrentFiltersIntoSchedule}
                >
                  Use current statement filters
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleHidePaid}
                    onChange={(e) => setScheduleHidePaid(e.target.checked)}
                    disabled={!scheduleEnabled}
                    className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Hide paid invoices</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleHidePaymentsColumn}
                    onChange={(e) => setScheduleHidePaymentsColumn(e.target.checked)}
                    disabled={!scheduleEnabled}
                    className="rounded border-gray-300 dark:border-gray-600 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] disabled:opacity-50"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Hide payments column</span>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 shrink-0">Period</span>
                <Select
                  value={schedulePeriod}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSchedulePeriod(v);
                    setShowScheduleCustomRange(v === 'custom');
                  }}
                  disabled={!scheduleEnabled}
                  className="h-9 w-full sm:w-[11rem] text-xs border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="1m">1 month</option>
                  <option value="3m">Last 3 months</option>
                  <option value="6m">Last 6 months</option>
                  <option value="12m">Last 12 months</option>
                  <option value="all">All time</option>
                  <option value="custom">Custom range</option>
                </Select>
              </div>
              {showScheduleCustomRange && (
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
                    <input
                      type="date"
                      value={scheduleCustomFrom}
                      onChange={(e) => setScheduleCustomFrom(e.target.value)}
                      disabled={!scheduleEnabled}
                      className="h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
                    <input
                      type="date"
                      value={scheduleCustomTo}
                      onChange={(e) => setScheduleCustomTo(e.target.value)}
                      disabled={!scheduleEnabled}
                      className="h-9 px-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs disabled:opacity-50"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                className="h-9 text-xs gap-2"
                disabled={scheduleSaving || !id}
                onClick={() => void saveStatementSchedule()}
              >
                {scheduleSaving ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                    Saving…
                  </>
                ) : (
                  'Save automation'
                )}
              </Button>
            </div>
          </div>

          {/* Printable statement — light “paper” for contrast in dark app mode */}
          <div
            id="client-statement-document"
            className="bg-white text-gray-900 border border-gray-200 rounded-lg shadow-sm p-6 sm:p-8 print:shadow-none print:border-gray-400"
          >
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between border-b border-gray-200 pb-6 mb-6">
              <div className="text-sm leading-relaxed space-y-0.5">
                {companyLines.length > 0 ? (
                  companyLines.map((line, i) => (
                    <div key={i} className={i === 0 ? 'font-semibold uppercase tracking-wide text-sm' : ''}>
                      {line}
                    </div>
                  ))
                ) : (
                  <div className="font-semibold text-gray-500">Add your company details in Settings</div>
                )}
              </div>
              <div className="flex flex-col items-center lg:items-center gap-2 lg:flex-1">
                <h2 className="text-2xl sm:text-3xl font-bold text-red-600 tracking-wide text-center">STATEMENT</h2>
              </div>
              <div className="flex justify-start lg:justify-end lg:w-48">
                {displayLogoSrc ? (
                  <img
                    src={displayLogoSrc}
                    alt=""
                    className="max-h-20 max-w-[8rem] object-contain rounded-full border border-gray-100"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-[10px] text-gray-400 text-center px-1">
                    Logo
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <div className="text-sm leading-relaxed space-y-0.5">
                {clientAddressLines.map((line, i) => (
                  <div key={i} className={i === 0 ? 'font-medium' : ''}>
                    {line}
                  </div>
                ))}
              </div>
              <div>
                <table className="w-full text-sm border border-gray-200">
                  <tbody>
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-200 w-[40%]">Date</td>
                      <td className="px-3 py-2 text-right border-b border-gray-200 font-medium">{statementDate}</td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-200">Invoiced</td>
                      <td className="px-3 py-2 text-right border-b border-gray-200">
                        <MoneyCell amount={summary.invoiced} />
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-200">Payments</td>
                      <td className="px-3 py-2 text-right border-b border-gray-200">
                        <MoneyCell amount={summary.payments} />
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-200">Credits</td>
                      <td className="px-3 py-2 text-right border-b border-gray-200 text-gray-400">—</td>
                    </tr>
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-3 py-2">Outstanding balance</td>
                      <td className="px-3 py-2 text-right">
                        <MoneyCell amount={summary.outstanding} className="font-semibold" />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="mt-4 text-xs text-gray-600">
                  Questions? Please contact us
                  {tenantSettings?.phone && (
                    <>
                      {' '}
                      on <span className="whitespace-nowrap">{tenantSettings.phone}</span>
                    </>
                  )}
                  {tenantSettings?.email && (
                    <>
                      {' '}
                      or{' '}
                      <a href={`mailto:${tenantSettings.email}`} className="text-blue-600 hover:underline">
                        {tenantSettings.email}
                      </a>
                    </>
                  )}
                  .
                </p>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200">
              <table className="w-full text-xs sm:text-sm border-collapse min-w-[640px]">
                <thead>
                  <tr className="bg-gray-100 text-left border-b border-gray-200">
                    <th className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap">Date</th>
                    <th className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap">Reference</th>
                    <th className="px-3 py-2 font-semibold text-gray-800">Description</th>
                    <th className="px-3 py-2 font-semibold text-gray-800 text-right whitespace-nowrap">Amount</th>
                    {!hidePaymentsColumn && (
                      <th className="px-3 py-2 font-semibold text-gray-800 text-right whitespace-nowrap">Paid</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={hidePaymentsColumn ? 4 : 5}
                        className="px-3 py-10 text-center text-gray-500"
                      >
                        No invoices match the current filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr key={r.id} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2 whitespace-nowrap align-top">{r.date}</td>
                        <td className="px-3 py-2 align-top">
                          <button
                            type="button"
                            onClick={() => navigate(`/tenant/invoices/${r.id}`)}
                            className="text-blue-600 hover:underline font-medium print:text-gray-900 print:no-underline"
                          >
                            #{r.num}
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top max-w-[14rem] sm:max-w-md">
                          <div className="flex items-start gap-1 min-w-0">
                            <span className="truncate" title={r.description}>
                              {r.description}
                            </span>
                            {r.hasNotes && (
                              <Paperclip className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" aria-hidden />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                          <MoneyCell amount={r.total} />
                        </td>
                        {!hidePaymentsColumn && (
                          <td className="px-3 py-2 text-right align-top whitespace-nowrap">
                            {r.paid > 0 ? <MoneyCell amount={r.paid} /> : <span className="text-gray-300">—</span>}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 border border-gray-200 rounded-md overflow-hidden">
              <div className="flex justify-between px-4 py-3 text-sm border-b border-gray-100">
                <span className="text-gray-700">Payments received</span>
                <MoneyCell amount={summary.payments} />
              </div>
              <div className="flex justify-between px-4 py-3 text-sm font-semibold bg-gray-50">
                <span>Outstanding balance</span>
                <MoneyCell amount={summary.outstanding} className="font-semibold" />
              </div>
            </div>
          </div>
        </div>

        {sendEmailModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-2xl w-full my-8 p-4 sm:p-5 shadow-xl border border-[var(--ds-border-subtle)] text-[12px] leading-normal">
              <div className="flex items-center justify-between mb-4 gap-2">
                <h2 className="text-xs font-bold leading-tight text-gray-900 dark:text-gray-100">
                  Send statement by email
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setSendEmailModalOpen(false);
                    setSendEmailAddresses([]);
                    setSendEmailSuccess('');
                    setSendModalError('');
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-0.5 rounded-md shrink-0"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>

              {sendModalError && (
                <div className="mb-3 p-3 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
                  <p className="text-xs text-[var(--ds-status-danger)]">{sendModalError}</p>
                </div>
              )}

              {sendEmailSuccess ? (
                <div className="py-1">
                  <div className="bg-[var(--ds-status-success)]/10 border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-3">
                    <p className="text-xs text-[var(--ds-status-success)]">{sendEmailSuccess}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-[var(--ds-text-muted)]">
                    Recipients see the same period and filters as on this page. The link expires in 30 days.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,6.5rem)_1fr] gap-2 sm:gap-3 sm:items-start">
                    <span className="text-xs font-semibold text-[var(--ds-text-primary)] pt-1.5 sm:pt-2">
                      Send to
                    </span>
                    <div>
                      {availableContacts.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 italic leading-snug">
                          No email addresses on file for this client. Add contacts on the client record.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {availableContacts.map((contact) => {
                            const selected = sendEmailAddresses.includes(contact.email);
                            return (
                              <button
                                key={contact.id}
                                type="button"
                                onClick={() => toggleRecipientEmail(contact.email)}
                                className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border ${
                                  selected
                                    ? 'bg-emerald-600 border-emerald-600 text-white dark:bg-emerald-600 dark:border-emerald-600'
                                    : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                                }`}
                              >
                                {contact.name}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            disabled={!user?.email}
                            title={user?.email ? 'Send a copy to your login email' : 'Your account has no email on file'}
                            onClick={() => setCcSelf((v) => !v)}
                            className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border disabled:opacity-40 disabled:cursor-not-allowed ${
                              ccSelf
                                ? 'bg-emerald-600 border-emerald-600 text-white dark:bg-emerald-600 dark:border-emerald-600'
                                : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                            }`}
                          >
                            CC me
                          </button>
                        </div>
                      )}
                    </div>

                    <span className="text-xs font-semibold text-[var(--ds-text-primary)] pt-1.5 sm:pt-2">Subject</span>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 min-w-0">
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)]"
                      />
                      <label className="flex items-center gap-1.5 shrink-0 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={attachPdf}
                          onChange={(e) => setAttachPdf(e.target.checked)}
                          className="h-3.5 w-3.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 accent-[var(--ds-accent-primary)]"
                        />
                        <span title="Attaches a PDF snapshot of this statement (same layout as the public link).">
                          Attach statement as PDF
                        </span>
                      </label>
                    </div>

                    <span className="text-xs font-semibold text-[var(--ds-text-primary)] sm:pt-1.5">Message</span>
                    <div className="min-w-0">
                      <p className="text-xs text-blue-600 dark:text-blue-400 mb-1 leading-snug">
                        <Link
                          to={`/tenant/settings?tab=routine-emails&expand=${encodeURIComponent(ROUTINE_EMAIL_TEMPLATE_KEYS.statement)}`}
                          className="underline font-medium hover:text-blue-800 dark:hover:text-blue-300"
                        >
                          Edit defaults in Routine emails (Settings)
                        </Link>
                        <span className="text-[var(--ds-text-secondary)]">
                          {' '}
                          — you can still change this message before sending.
                        </span>
                      </p>
                      <textarea
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        rows={9}
                        className="w-full px-2 py-1.5 text-xs leading-relaxed border border-gray-300 dark:border-gray-600 rounded-md bg-amber-50 dark:bg-amber-950/25 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)] font-sans"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setSendEmailModalOpen(false);
                        setSendEmailAddresses([]);
                        setSendModalError('');
                      }}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={() => void handleCopyStatementLink()}
                      disabled={!id || loading}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {copyLinkState === 'copied' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-[var(--ds-status-success)] shrink-0" aria-hidden />
                          Link copied
                        </>
                      ) : (
                        <>
                          <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Copy statement link
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => void handleSendStatementEmail()}
                      disabled={sendEmailLoading || sendEmailAddresses.length === 0}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {sendEmailLoading ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Layout>
    </TooltipProvider>
  );
}
